import React, { useCallback, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { MapActions, MapContext } from "context/map";
import { X } from "phosphor-react";
import { useZonesQuery } from "hooks/useZones";
import Zones from "../Zones";
import Rates from "../Rates";
import "./index.scss";

const ZonePanel = ({ setZoneLayerLoading, setZoneLayerBackgroundLoading }) => {
  const { state, dispatch } = useContext(MapContext);
  const {
    map,
    selectedLayer,
    selectedAlgorithm,
    selectedDate,
    selectedFields,
  } = state;
  const [activeTab, setActiveTab] = useState("zones");
  const [numberOfZones, setNumberOfZones] = useState(null);
  const [resolutionLevel, setResolutionLevel] = useState(null);
  const [zoneRates, setZoneRates] = useState({});
  const captureDateQuery = `&latest_capture_date=${selectedDate}`;
  const zonesQuery = `&zones=${numberOfZones}`;
  const resolutionQuery = `&resolution=${resolutionLevel}`;
  const algorithmQuery = `&algorithm=${selectedLayer}_${selectedAlgorithm.toLocaleLowerCase()}`;
  const query = `${captureDateQuery}${zonesQuery}${resolutionQuery}${algorithmQuery}`;

  const { data } = useZonesQuery(query, selectedFields);

  const removeZones = useCallback(() => {
    const zonesLayer = map.getLayer("zones");
    const zonesOutlineLayer = map.getLayer("zones-outline");
    const zoneSource = map.getSource("zones");

    if (zonesLayer) {
      map.removeLayer("zones");
    }

    if (zonesOutlineLayer) {
      map.removeLayer("zones-outline");
    }

    if (zoneSource) {
      map.removeSource("zones");
    }
  }, [map]);

  const handleGoBack = () => {
    removeZones();

    dispatch({
      type: MapActions.SET_VIEW_MODE,
    });
  };

  useEffect(() => {
    if (!numberOfZones && map.getSource("zones")) {
      map.getSource("zones").setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }, [numberOfZones, map]);

  useEffect(() => {
    if (selectedLayer || selectedAlgorithm) {
      setNumberOfZones(null);
      setResolutionLevel(null);
    }
  }, [dispatch, selectedAlgorithm, selectedLayer]);

  useEffect(() => {
    const newZoneRates = Array.from(
      { length: numberOfZones },
      (_, i) => i + 1
    ).reduce((obj, key) => {
      return { ...obj, [key]: null };
    }, {});

    setZoneRates(newZoneRates);
  }, [numberOfZones]);

  const enableZoneFetching = useCallback(() => {
    if (selectedDate && numberOfZones && resolutionLevel) {
      dispatch({
        type: MapActions.SET_ZONE_LAYER_FETCHING,
        value: true,
      });
    }
  }, [dispatch, numberOfZones, resolutionLevel, selectedDate]);

  useEffect(() => {
    enableZoneFetching();
  }, [enableZoneFetching]);

  const gotoRatesTab = () => {
    const zoneSource = map.getSource("zones");
    // eslint-disable-next-line no-underscore-dangle
    if (zoneSource && zoneSource._data.features.length > 0) {
      if (!data) return;
      dispatch({
        type: MapActions.SET_ZONE_DATA,
        value: data,
      });
      setActiveTab("rates");
    }
  };

  const renderTabs = () => {
    return (
      <>
        <div className="zoning-tab">
          <span
            className={`zoning-tab-item ${
              activeTab === "zones" ? "-active" : ""
            }`}
            onClick={() => setActiveTab("zones")}
            aria-hidden
          >
            1. Map Settings
          </span>
          <span
            className={`zoning-tab-item ${
              activeTab === "rates" ? "-active" : ""
            }`}
            onClick={() => gotoRatesTab()}
            aria-hidden
          >
            2. Edit & Assign Rates
          </span>
        </div>
        {activeTab === "zones" ? (
          <Zones
            numberOfZones={numberOfZones}
            setNumberOfZones={(zone) => setNumberOfZones(zone)}
            resolutionLevel={resolutionLevel}
            setResolutionLevel={(resolution) => setResolutionLevel(resolution)}
            setActiveTab={(tab) => setActiveTab(tab)}
            setZoneLayerLoading={(loading) => setZoneLayerLoading(loading)}
            setZoneLayerBackgroundLoading={(loading) =>
              setZoneLayerBackgroundLoading(loading)
            }
          />
        ) : (
          <Rates
            resolutionLevel={resolutionLevel}
            numberOfZones={numberOfZones}
            zoneRates={zoneRates}
            setZoneRates={(newRate) =>
              setZoneRates((prevRate) => ({ ...prevRate, ...newRate }))
            }
          />
        )}
      </>
    );
  };

  return (
    <div className="zoning-process">
      <div className="zoning-header">
        <h1 className="title">Create Zones</h1>
        <button
          className="close-button"
          type="button"
          onClick={() => handleGoBack()}
        >
          <X size={20} color="#292b33" weight="bold" />
        </button>
      </div>
      {renderTabs()}
    </div>
  );
};

ZonePanel.propTypes = {
  setZoneLayerLoading: PropTypes.func.isRequired,
  setZoneLayerBackgroundLoading: PropTypes.func.isRequired,
};

export default ZonePanel;
